import { memo, useCallback, useEffect, useMemo } from 'react';
import { batch, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Fetching } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { globalActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';

import NoResults from '../../../SearchAll/ResultBlock/NoResults';
import Row from './Row';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

import './MainViewBlock.scss';

const REQUEST_ID = "Main_View_Statuses"
const useSearchStatuses = ({ query, type, isJobId, getQueryBuilder, shouldBeLimited }) =>
  useQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query: isJobId ? "*" : (query ? `*${query}*` : "*"),
        size: 10, 
        aggregate: true, 
        type, 
        jobId: isJobId ? query : undefined,
        REQUEST_ID
      }
    ),
    refetchInterval: () => shouldBeLimited() ? undefined : 5000,
  })
const EMPTY_ARRAY = [];
const MainViewBlock = ({ query = "", title = "", type = undefined }) => {
  const dispatch = useDispatch(),
        queryClient = useQueryClient(),
        isJobId = useMemo(() => query.match("([a-f0-9]{8}(-[a-f0-9]{4}){4}[a-f0-9]{8})"), [query]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data, isPending } = useSearchStatuses({ query, type, isJobId, getQueryBuilder, shouldBeLimited }),
        results = data?.items || EMPTY_ARRAY,
        resultsStyle = useMemo(() => {
          if(results.length > 0) {
            return {
              height: results.length * (32 + 4) + 2 * 16 - 16 + 32
            }
          } else if(results.length === 0) {
            return {
              height: 2 * 16 + 64
            }
          }
        }, [results]),
        seeAll = useCallback(() => batch(() => {
          dispatch(statusesActions.addToHistory())
          dispatch(statusesActions.changeValue(statusesActions.MODES.SEE_ALL, "mode"))
          dispatch(statusesActions.changeValue(type, "type"))
        }), [type, dispatch])

  useEffect(() => () => {
    queryClient.cancelQueries({ queryKey: [StatusesQuery.KEYS.SEARCH, REQUEST_ID] })
  }, [queryClient])

  return (
    <div className="statuses__main-view__content__block">
      <div className="statuses__main-view__content__block__header">
        <div className="statuses__main-view__content__block__header__name">
          {title}
        </div>
        <div className="statuses__main-view__content__block__header__line"/>
      </div>
      <div 
          className="statuses__main-view__content__block__body"
          style={resultsStyle}
      >
        <When condition={isPending}>
          <Fetching/>
        </When>
        <When condition={results?.length > 0 || isPending}>
          {() => results.map(({ name, startTime, status, jobId, payload, warnStatuses, componentStatuses }) => (
            <Row
                componentStatuses={componentStatuses}
                jobId={jobId}
                key={"Status " + jobId}
                name={type === StatusesEnums.STATUS_TYPE.ETL ? (payload?.groupName || name) : name}
                payload={payload}
                startTime={startTime}
                status={status}
                type={type}
                warnStatuses={warnStatuses}
            />
          ))}
        </When>
        <When condition={results?.length > 0}>
          {() => (
            <div 
                className="statuses__main-view__content__block__body__see-all"
                onClick={seeAll}
            >
              See all results
            </div>
          )}
        </When>
        <When condition={results?.length === 0 && !isPending}>
          {() => (
            <div className="statuses__main-view__content__block__body__container">
              <NoResults/>
            </div>
          )}
        </When>
      </div>
    </div>
  )
}

MainViewBlock.propTypes = {
  query: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
}

export default memo(MainViewBlock)